import React, { useMemo } from 'react';
import { Alert, Card, Grid, Progress, Space, Tag, Tooltip, Typography } from 'antd';
import { useQuery } from '@tanstack/react-query';
import {
  commercialLifecycleService,
  type CommercialLifecycleContext,
  type CommercialLifecycleDefinition,
} from '@/services/commercialLifecycle.service';
import {
  buildLifecycleStages,
  lifecycleStatusColor,
  lifecycleStatusGlyph,
  type LifecycleStageView,
} from '@/components/commercial-lifecycle/lifecycleStageView';

const { Text } = Typography;
const { useBreakpoint } = Grid;

export type { LifecycleStageView };
export type TimelineStageStatus = import('@/components/commercial-lifecycle/lifecycleStageView').LifecycleStageStatus;
export type TimelineStageView = LifecycleStageView;

export interface CommercialLifecycleTimelineProps {
  entityType: string;
  entityId: string;
  /** Prefer horizontal on wide screens; vertical when forced or narrow. */
  orientation?: 'auto' | 'horizontal' | 'vertical';
}

export const CommercialLifecycleTimeline: React.FC<CommercialLifecycleTimelineProps> = ({
  entityType,
  entityId,
  orientation = 'auto',
}) => {
  const screens = useBreakpoint();

  const contextQ = useQuery({
    queryKey: ['commercial-lifecycle-context', entityType, entityId],
    queryFn: () => commercialLifecycleService.getContext(entityType, entityId),
    enabled: Boolean(entityType && entityId),
    staleTime: 30_000,
    retry: false,
  });

  const definitionsQ = useQuery({
    queryKey: ['commercial-lifecycle-definitions'],
    queryFn: () => commercialLifecycleService.getDefinitions(),
    enabled: Boolean(contextQ.data?.lifecycle?.definitionCode),
    staleTime: 60_000,
    retry: false,
  });

  const stages = useMemo(() => {
    if (!contextQ.data) return [];
    const code = contextQ.data.lifecycle?.definitionCode;
    const def = (definitionsQ.data || []).find((d: CommercialLifecycleDefinition) => d.code === code);
    return buildLifecycleStages(contextQ.data as CommercialLifecycleContext, def);
  }, [contextQ.data, definitionsQ.data]);

  if (contextQ.isError) {
    return null; // soft-fail
  }

  if (contextQ.isLoading || !contextQ.data) {
    return null;
  }

  const ctx = contextQ.data;
  if (ctx.disabled || ctx.lifecycle?.status === 'disabled') {
    return null;
  }

  if (ctx.lifecycle?.status === 'unresolved' && !stages.length) {
    return (
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Commercial lifecycle not configured for this company"
        description={(ctx.lifecycle.diagnostics || []).join(' ') || undefined}
      />
    );
  }

  const vertical =
    orientation === 'vertical' || (orientation === 'auto' && !screens.md);

  return (
    <Card size="small" style={{ marginBottom: 16 }} styles={{ body: { padding: '12px 16px' } }}>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 8 }} wrap>
        <Space wrap>
          <Text strong>{ctx.lifecycle?.title || 'Commercial lifecycle'}</Text>
          {ctx.lifecycle?.definitionCode ? (
            <Tag>{ctx.lifecycle.definitionCode}</Tag>
          ) : null}
          {ctx.currentStage?.requiresApproval ? (
            <Tag color="orange">Approval pending</Tag>
          ) : null}
        </Space>
        <Progress
          percent={ctx.progress?.percent ?? 0}
          size="small"
          style={{ width: 140, marginBottom: 0 }}
          format={(p) => `${p}%`}
        />
      </Space>

      <div
        style={
          vertical
            ? { display: 'flex', flexDirection: 'column', gap: 8 }
            : {
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: '4px 0',
                overflowX: 'auto',
              }
        }
      >
        {stages.map((s, i) => (
          <React.Fragment key={s.stageKey}>
            {!vertical && i > 0 ? (
              <Text type="secondary" style={{ margin: '0 6px' }}>
                →
              </Text>
            ) : null}
            <Tooltip
              title={
                [
                  s.label,
                  s.status.replace('_', ' '),
                  s.isOptional ? 'optional' : null,
                  s.requiresApproval ? 'requires approval' : null,
                  s.entityType ? `entity: ${s.entityType}` : null,
                ]
                  .filter(Boolean)
                  .join(' · ')
              }
            >
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '2px 8px',
                  borderRadius: 4,
                  background:
                    s.status === 'current' || s.status === 'approval_pending'
                      ? 'rgba(22, 119, 255, 0.08)'
                      : undefined,
                  border:
                    s.status === 'blocked' ? '1px solid #ffccc7' : '1px solid transparent',
                }}
              >
                <span style={{ color: lifecycleStatusColor(s.status), fontWeight: 600 }}>
                  {lifecycleStatusGlyph(s.status)}
                </span>
                <Text
                  style={{
                    fontSize: 12,
                    color: lifecycleStatusColor(s.status),
                    fontWeight:
                      s.status === 'current' || s.status === 'approval_pending' ? 600 : 400,
                  }}
                >
                  {s.label}
                  {s.isOptional && s.status === 'optional' ? ' (opt)' : ''}
                </Text>
              </span>
            </Tooltip>
          </React.Fragment>
        ))}
      </div>
    </Card>
  );
};

export default CommercialLifecycleTimeline;
